import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import LiveDigitalCertificateView, {
  buildLiveCertificateData,
} from '../../components/Students/LiveDigitalCertificateView';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const parseStudentData = () => {
  try {
    const raw = localStorage.getItem('student_data');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function StudentLiveCertificatePage() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(parseStudentData);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseTab, setCourseTab] = useState(0);

  const fetchProgress = useCallback(async () => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students/me/live-certificate-progress`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.message || 'Failed to load progress');
      }

      setProgressData(response.data);
      setCourseTab(0);

      if (response.data?.student) {
        const apiStudent = response.data.student;
        const cached = parseStudentData() || {};
        const merged = {
          ...cached,
          id: apiStudent.id || cached.id,
          student_id: apiStudent.studentId || apiStudent.student_id || cached.student_id,
          name: apiStudent.name || cached.name,
          phone: apiStudent.phone || cached.phone,
          enrolled_courses:
            apiStudent.enrolled_courses ||
            response.data.courses?.map((c) => c.courseCode) ||
            cached.enrolled_courses,
          photo:
            apiStudent.photo ||
            apiStudent.photo_url ||
            cached.photo,
        };
        setStudent(merged);
        localStorage.setItem('student_data', JSON.stringify(merged));
      } else if (response.data?.courses?.length) {
        const cached = parseStudentData() || {};
        const merged = {
          ...cached,
          enrolled_courses:
            response.data.courses.map((c) => c.courseCode) || cached.enrolled_courses,
        };
        setStudent(merged);
      }
    } catch (err) {
      console.error('Live certificate fetch error:', err);
      if (err?.response?.status === 401) {
        localStorage.removeItem('student_token');
        navigate('/student/login');
        return;
      }
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load live certificate',
      );
      setProgressData(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchProgress();
  }, [fetchProgress, navigate]);

  const livePreview = buildLiveCertificateData(student, loading ? null : progressData);

  if (!localStorage.getItem('student_token')) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 52%, #F5F3FF 100%)',
        pb: 4,
      }}
    >
      <Box
        sx={{
          bgcolor: '#1E1B4B',
          color: '#fff',
          py: 2,
          px: { xs: 2, md: 3 },
          mb: 3,
          boxShadow: '0 8px 32px rgba(30,27,75,0.35)',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => navigate('/student/dashboard')}
                sx={{ color: '#fff' }}
                aria-label="Back to dashboard"
              >
                <ArrowBackIcon />
              </IconButton>
              <EmojiEventsIcon sx={{ color: '#FBBF24' }} />
              <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                Live Digital Certificate
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={loading ? 'Loading...' : `${livePreview.overall}% Ready`}
                sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 800 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={fetchProgress}
                disabled={loading}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.85, pl: { xs: 0, sm: 6 } }}>
            Your progress only — live from LMS activity
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {!student && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LiveDigitalCertificateView
            student={student || { name: 'Student' }}
            progressData={progressData}
            loading={loading}
            error={error}
            courseTab={courseTab}
            onCourseTabChange={setCourseTab}
            onRefresh={fetchProgress}
          />
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/student/dashboard')}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
